import ReplyIcon from "@mui/icons-material/Reply";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";
import { useDisclosure, useRoleContext } from "hooks";
import { secondsSince } from "utils";

import { SubmitAnswerMenu } from "../SubmitAnswerMenu";
import { removeDuplicateNewLines } from "./utils";

interface Props {
  qa: QuoteQA;
  shipmentId: number;
}

export function QuoteQADisplay(props: Props) {
  const { qa, shipmentId } = props;
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const handleAnswered = () => {
    posthog?.capture(ClientAppEvent.QUOTE_QUESTION_ANSWERED, {
      question_id: qa.question.id,
      quote_id: qa.question.quote,
      shipment_id: shipmentId,
      role_type: role?.type,
      source: "CUSTOMER_APP" satisfies SourceType,
      seconds_to_answer: secondsSince(qa.question.created_at),
    });
  };

  const getReplyButton = () => {
    // Only render reply button if user is a carrier AND question has not been answered already
    if (role?.type === "CARRIER" && qa.answer == null) {
      return (
        <Box sx={{ ml: "1rem" }}>
          {isOpen ? (
            <Button onClick={onClose}>Cancel</Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              onClick={onOpen}
              startIcon={<ReplyIcon />}
            >
              Reply
            </Button>
          )}
        </Box>
      );
    }
    return null;
  };

  const getAnswer = () =>
    qa.answer != null ? (
      <Stack
        id="answer-container"
        direction="row"
        alignContent="flex-start"
        ml=".5rem"
        alignItems="flex-end"
      >
        <Stack direction="column" flexWrap="wrap">
          <Divider variant="middle" />
          <Typography
            variant="body1"
            sx={{
              overflow: "hidden",
              wordBreak: "break-word",
              maxWidth: "100%",
              whiteSpace: "pre-line",
            }}
          >
            <u>
              <b>A</b>:
            </u>{" "}
            {removeDuplicateNewLines(qa?.answer?.text)}
          </Typography>
        </Stack>
      </Stack>
    ) : null;

  return (
    <Stack id="root-container" direction="column" spacing={1}>
      <Stack justifyContent="space-between" direction="row" alignItems="center">
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-line",
            overflow: "hidden",
            wordBreak: "break-word",
            maxWidth: "100%",
          }}
        >
          <b> Q: </b>
          {removeDuplicateNewLines(qa?.question?.text)}
        </Typography>
        <>{getReplyButton()}</>
      </Stack>
      {getAnswer()}
      {isOpen && (
        <SubmitAnswerMenu
          postQuestionURL={`/quoteQAs/${qa.question.id}/answers`}
          onAnswered={handleAnswered}
        />
      )}
      <Divider sx={{ mt: "1rem" }} />
    </Stack>
  );
}
